/**
 * Labstep
 */

import { ICONS } from 'labstep-web/constants/icons';
import type { ColDef } from 'labstep-web/core/DataGrid/types';
import { setValue } from 'labstep-web/core/DataGrid/utils';
import type { ProtocolConditionChild } from 'labstep-web/models';
import type { Entity } from 'labstep-web/models/entity.model';
import { ProtocolCondition } from 'labstep-web/models/protocol-condition.model';
import type { ProtocolValue } from 'labstep-web/models/protocol-value.model';
import {
  validateNumeric,
  validateUnit,
} from 'labstep-web/services/amount-unit.service';
import { isValid } from 'labstep-web/services/validation';
import rules from 'labstep-web/services/validation/rules';
import { makeSelectHasAccessCreate } from 'labstep-web/state/new/authorization';
import store from 'labstep-web/state/store';
import type { colDefAmountParametersType } from './types';

const RULE_AMOUNT = rules.protocol_value.amount;
const RULE_UNIT = rules.protocol_value.unit;

const validate = (newValue: unknown, isAmount: boolean) => {
  const value = isAmount
    ? validateNumeric(newValue)
    : validateUnit(newValue);
  return {
    value,
    error:
      value === false ||
      !isValid(value, isAmount ? RULE_AMOUNT : RULE_UNIT),
  };
};

export const getHeaderParams = (
  isInput: boolean,
): Pick<
  ColDef<ProtocolValue>,
  'headerName' | 'headerComponentParams'
> => ({
  headerName: isInput ? 'Amount Used' : 'Initial Amount',
  headerComponentParams: {
    icon: isInput
      ? ICONS.protocol_value.info.amount_used
      : ICONS.protocol_value.info.amount_created,
  },
});

export const getValueSetter =
  (
    getEntity: colDefAmountParametersType<Entity>['getNestedEntity'],
    updateUnit?: boolean,
    variableTemplate?: ProtocolConditionChild,
    experimentId?: number,
    protocolId?: number,
  ) =>
  (params: any): boolean => {
    const entity = getEntity?.(params);
    const validationAmount = validate(params.newValue, true);
    const protocolCondition =
      params.data instanceof ProtocolCondition
        ? params.data
        : undefined;

    if (validationAmount.error) {
      return false;
    }
    const body: { [key: string]: any } = {
      ...(experimentId && { experiment_id: experimentId }),
      ...(protocolId && { protocol_id: protocolId }),
      ...(variableTemplate?.guid && {
        variable_template_guid: variableTemplate.guid,
      }),
      ...(protocolCondition?.id && {
        protocol_condition_id: protocolCondition.id,
      }),
      amount: validationAmount.value,
      ...(variableTemplate?.unit && { unit: variableTemplate.unit }),
    };

    let createOrUpdateProps: any = {
      entityName: 'protocol_value',
      createProps: {
        createBody: body,
        parentName: protocolCondition
          ? 'protocol_condition'
          : undefined,
        parentId: protocolCondition?.guid,
      },
    };

    if (entity) {
      createOrUpdateProps = {
        entityName: entity.entityName,
        id: entity.idAttr,
      };
    }

    let didUpdate = setValue(
      'amount',
      validationAmount.value,
      createOrUpdateProps,
    );

    if (updateUnit) {
      const validationUnit = validate(params.newValue, false);
      if (validationUnit.error) {
        return false;
      }
      didUpdate =
        setValue('unit', validationUnit.value, createOrUpdateProps) ||
        didUpdate;
    }
    return didUpdate;
  };

export const getCanCreateProtocolValue = (
  experimentId?: number,
  protocolId?: number,
) => {
  const selectHasAccessCreate = makeSelectHasAccessCreate();
  const hasAccess = selectHasAccessCreate(
    store.getState() as any,
    'protocol_value',
    experimentId ? 'experiment' : 'protocol',
    experimentId || protocolId,
  );

  return hasAccess;
};
